import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import { useIsMobile } from '@/hooks/use-mobile'
import { attestationService } from '@/services/attestationService'
import { AttestationScreenDesktop } from './AttestationScreenDesktop'
import { AttestationScreenMobile } from './AttestationScreenMobile'
import { getDefaultFormData, type AttestationScreenBaseProps } from './AttestationScreenBase'

/**
 * AttestationScreen component
 * Renders mobile or desktop version based on screen size
 * 
 * Uses component split pattern for significantly different layouts:
 * - Mobile: Compact vertical layout
 * - Desktop: Centered layout with absolute positioning
 * 
 * Manages form state, validation, and submission logic
 */
const AttestationScreen = () => {
  const isMobile = useIsMobile()
  const [formData, setFormData] = useState(getDefaultFormData())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [isCheckingStatus, setIsCheckingStatus] = useState(true)

  useEffect(() => {
    // Skip attestation check since it's already done in App.tsx
    setIsCheckingStatus(false)
  }, [])

  const handleFormDataChange = (updates: Partial<typeof formData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
    // Clear error when user starts typing
    if (error) setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await attestationService.createAttestation(formData)
      // Redirect to profile preferences after attestation
      // Use window.location to force a full page reload and update attestation status
      window.location.href = '/profile-preferences'
    } catch (err: any) {
      setError(err.message || 'Failed to submit attestation. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (isCheckingStatus) {
    return (
      <div className="min-h-screen bg-[var(--background)] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary-500" />
          <h2 className="text-xl font-semibold text-[var(--text)]">Checking attestation status...</h2>
        </motion.div>
      </div>
    )
  }

  const baseProps: AttestationScreenBaseProps = {
    formData,
    loading,
    error,
    onFormDataChange: handleFormDataChange,
    onSubmit: handleSubmit,
  }

  // Component Split pattern - completely separate mobile and desktop rendering
  if (isMobile) {
    return <AttestationScreenMobile {...baseProps} />
  }

  return <AttestationScreenDesktop {...baseProps} />
}

export default AttestationScreen

